// The poison order on a spy's row (module `espionage.ops`): one button per
// well or granary the spy could foul in a target city.
//
// The sim lists the supplies and says which are fouled already; the cost
// comes from the snapshot. A refusal comes back as `false`.

import type { PlayerHudEntry } from '@aod/ui';
import { useState } from 'react';
import type { EspionageOps, Spy, SpyTarget } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { FramePicker } from './FramePicker.js';

export interface PoisonControlsProps {
  readonly ops: EspionageOps;
  readonly spy: Spy;
  readonly target: SpyTarget;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  readonly onPoison: (
    unitId: string,
    cityId: string,
    supply: 'well' | 'granary',
    frameSeat?: number,
  ) => boolean;
}

export function PoisonControls({
  ops,
  spy,
  target,
  players,
  localPlayerIndex,
  onPoison,
}: PoisonControlsProps) {
  const t = useT();
  const [frame, setFrame] = useState<number | null>(null);
  const [refused, setRefused] = useState(false);
  const supplies = target.supplies ?? [];
  if (!ops.poison || supplies.length === 0) return null;
  const suspects = players.filter(
    (p) => p.playerId !== localPlayerIndex && p.playerId !== target.ownerId,
  );
  const nameOf = (kind: 'well' | 'granary') =>
    t(kind === 'well' ? 'espionage.supplyWell' : 'espionage.supplyGranary');
  return (
    <div className="flex flex-col gap-1" data-testid={`spy-poison-${spy.unitId}-${target.cityId}`}>
      <FramePicker
        testId={`spy-poison-frame-${spy.unitId}-${target.cityId}`}
        falseFlag={ops.falseFlag}
        suspects={suspects}
        value={frame}
        onChange={setFrame}
      />
      {supplies.map((supply) =>
        supply.poisoned ? (
          <span
            key={supply.kind}
            className="text-[10px] text-muted-foreground"
            data-testid={`spy-poisoned-${target.cityId}-${supply.kind}`}
          >
            {t('espionage.poisoned', { supply: nameOf(supply.kind) })}
          </span>
        ) : (
          <button
            key={supply.kind}
            type="button"
            data-testid={`spy-poison-${spy.unitId}-${target.cityId}-${supply.kind}`}
            {...appDom({
              id: `spy-poison-${spy.unitId}-${target.cityId}-${supply.kind}`,
              type: 'Button',
              role: 'button',
              scope: 'section',
            })}
            onClick={() => {
              const ok = onPoison(
                spy.unitId,
                target.cityId,
                supply.kind,
                frame === null ? undefined : frame,
              );
              setRefused(!ok);
            }}
            className="shrink-0 rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60"
          >
            {t('espionage.poison', { supply: nameOf(supply.kind), gold: ops.poisonGold })}
          </button>
        ),
      )}
      {refused && (
        <output className="text-[10px] text-destructive">{t('espionage.poisonRefused')}</output>
      )}
    </div>
  );
}
